import React, {useContext, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useWindowDimensions, Platform, Linking} from 'react-native';
import {Bet, User} from '../../../../generated/smg_service_pb';
import MainStyle from '../../../../MainStyle';
import AsyncStorage from "@react-native-async-storage/async-storage";
import {StoreContext} from "../../../components/StoreContextProvider";
import useIdleLogout from "../../../hooks/useIdleLogout";
import useFullSwingAcceptedBetRefresh from "../../../hooks/useFullSwingAcceptedBetRefresh";
import useFullSwingNextBetEpisode, {reportNextBetEvent} from "../../../hooks/useFullSwingNextBetEpisode";
import {getUserAccessBlock} from "../../../utils/userAccess";
import {PartnerKioskUpdate_UserSessionUpdateData_UserSessionState} from "../../../../generated/partner_service_pb";
import {AutoPlayBalanceStatus, WagerMode} from "../../../../generated/bay_session_shared_pb";
import {withTimeout} from "../../../utils/withTimeout";
import OpenBets from "../../../components/OpenBets";
import {useFormik} from "formik";
import * as Yup from "yup";
import {
  LANDING_PAGE_URL, SESSION_EXPIRE_TIME, Landing, smgEnvironment, tabletBreakpoint, BrandName, isFullSwing,
  StartingPage
} from "../../../constants";
import moment from 'moment';
import {useIsFocused, useNavigation, useRoute} from '@react-navigation/native';
import {useSafeAreaInsets} from "react-native-safe-area-context";
import ProtectionAndDisclaimer from "../../../components/ProtectionAndDisclaimer";
import {colors} from "../../../assets/styles/colors";
import ServiceClient from "../../../utils/ServiceClient";
import {CancelModalProps} from "../../../types";
import ContinueAutoPlayModal from "../../../modals/ContinueAutoPlay";
import NewHeader from "../../../components/NewHeader";
import BottomNavigation from "../../../components/BottomNavigation";
import TournamentRegistrationConfirmationModal from "../../../modals/TournamentRegistrationConfirmationModal";
import AlertModal from "../../../modals/AlertModal";
import Div from "../../../lib/Div";
import FullSwingHomePage from "../../FullSwingHomePage";
import VerifyIdentityModal from "../../../modals/VerifyIdentityModal";
import {NativeStackNavigationProp} from "@react-navigation/native-stack";
import ShortTimerModal from "../../../modals/ShortTimerModal";
import FullSwingInfoModal from "../../../modals/FullSwingInfoModal";
import {track} from "../../../services/analytics";

const NON_FULL_SWING_REFRESH_INTERVAL_MS = 10_000;
const NON_FULL_SWING_OPEN_RETRY_DELAY_MS = 1500;
const NON_FULL_SWING_OPEN_RETRY_ATTEMPTS = 3;
// SMG-4387: retries for the FullSwing open-bet fallback. Fewer attempts than
// the consumer skin because the bay-session stream, not a poll, is what tells
// us the accepted bet went away — there is no "smg-core may expose it a moment
// later" window to cover, only the engine fill that follows the mint.
const FULL_SWING_OPEN_RETRY_ATTEMPTS = 2;
// SMG-4201: ServiceClient.getBets has no timeout, so the requests of "Check again" have their own: one that hangs must not
// leave the button disabled for ever.
const NEXT_BET_REQUEST_TIMEOUT_MS = 10_000;
const UserSessionState = PartnerKioskUpdate_UserSessionUpdateData_UserSessionState;

const getBetTypesFromBets = (bets: Bet[]) => {
  const betTypes: string[] = [];

  bets.forEach(bet => {
    if (!betTypes.includes(bet.betType)) betTypes.push(bet.betType);
  });

  return betTypes;
};

const filterVisibleBets = (bets: Bet[]) => {
  // show only old game on external
  if (smgEnvironment !== 'dev') return bets.filter(bet => bet.betType === 'paytable');
  return bets;
};

// Only text raised through this class reaches the player. Everything else — a
// ConnectError in particular — carries wire detail like "[failed_precondition]
// bet 41 is not cancellable", which is noise to the player and leaks server
// internals into the UI.
class CancelBetMessage extends Error {
}

const BetSlip = () => {
  const navigation: NativeStackNavigationProp<any> = useNavigation();
  const Context = useContext(StoreContext);
  const {
    cancelModalConfig, user, extendedUserData, sessionBackend, bayActiveBetId, bayActiveBetAmountMinor, userSessionData,
    insufficientFundsNoFallback, bayAutoPlayBalanceStatus, activeWagerMode
  } = Context;
  const formik = useFormik({
    initialValues: {contestAmount: ''},
    validationSchema: Yup.object({contestAmount: Yup.string().required().matches(/^[0-9]*(\.[0-9]{0,2})?$/, 'invalid')}),
    onSubmit: () => console.log('onSubmit')
  });
  const {width} = useWindowDimensions();
  const [BetTypes, setBetTypes] = useState<string[]>([]);
  const [currBetType, setCurrBetType] = useState('paytable');
  const [betsList, setBetsList] = useState<Bet[]>([]);
  const [activeBet, setActiveBet] = useState<Bet>();
  const isMd = width >= tabletBreakpoint;
  const isFocused = useIsFocused();
  const insets = useSafeAreaInsets();
  const init = useRef(false);
  const route = useRoute();
  const [continueAutoPlay, setContinueAutoPlay] = useState(false);
  const AUTO_PLAY_MODAL_COOLDOWN_MS = 30_000;
  const autoPlayModalCooldownRef = useRef<number>(0);
  const [confirmModalType, setConfirmModalType] = useState<'StartAutoPlay' | 'StopAutoPlay' | ''>('');
  const [noBetsAvailable, setNoBetsAvailable] = useState(false);
  const noBetsAvailableRef = useRef(false);
  const [alertModalConfig, setAlertModalConfig] = useState({
    isOpen: false, title: '', message: '', firstButtonText: '', hideSecondButton: false
  });
  const openBetRetryTimeoutRef = useRef<any>();
  // SMG-4201: ends the pause between two tries of the open-bet search right away (see clearOpenBetRetry).
  const openBetRetryResolveRef = useRef<(() => void) | undefined>(undefined);
  const nonFullSwingRefreshTimeoutRef = useRef<any>();
  const nonFullSwingBetRefreshInFlightRef = useRef(false);
  // SMG-4387: guards the FullSwing open-bet fallback. The stream effect can
  // fire repeatedly while active_bet_id stays empty (a wager-amount frame is
  // enough), and each run schedules retries; without this the retry chains
  // stack up and race each other into setActiveBet.
  const fullSwingOpenBetInFlightRef = useRef(false);
  // SMG-4201: how the last one-shot search that did not find a bet ended, and what the last look for an open bet saw,
  // for diagnostics (a search that finds a bet ends the wait, so there is nothing to explain).
  const openBetSearchResultRef = useRef<'empty' | 'error' | undefined>(undefined);
  const openBetLookRef = useRef<{rawCount: number, visibleCount: number} | undefined>(undefined);
  // SMG-4201: what this screen saw after the last cancel, for the report of a long wait (see noteCancellation).
  const afterCancelRef = useRef<{ at: number, streamIdAtAck: boolean, streamChanges: number, streamIdClearedAfterSec?: number, openLooks: number, acceptedLooks: number } | undefined>(undefined);
  // SMG-4201: a bet is on screen, or in betsList on its way to activeBet. Assigned during render so that a response that
  // arrives later sees the current value.
  const betAppliedRef = useRef(false);
  // SMG-4201: the player is waiting for a bet right now (latest render), for answers that arrive later.
  const waitingForNextBetRef = useRef(false);
  // SMG-4201: the screen is gone. An answer that comes after that must not touch the global loading overlay of another screen.
  const unmountedRef = useRef(false);
  // SMG-4201: AutoPlay as of the latest render, for answers that arrive later (see checkNextBetAgain).
  const autoPlayEnabledRef = useRef(false);
  autoPlayEnabledRef.current = !!user?.autoPlayEnabled;
  useEffect(() => {
    unmountedRef.current = false;

    return () => {
      unmountedRef.current = true;
    };
  }, []);
  // SMG-4387: fences the open-bet retry recursion; bumped by clearOpenBetRetry.
  const openBetFetchGenerationRef = useRef(0);
  const [openKYCModal, setOpenKYCModal] = useState(false);
  const [openShortTimerModal, setOpenShortTimerModal] = useState(false);
  const [showAgeRestrictionModal, setShowAgeRestrictionModal] = useState(false);
  const lastAcceptedBetRef = useRef<Bet | undefined>(undefined);

  const isFreePlay = extendedUserData.isNewPlayer || extendedUserData.freeShotNumber !== extendedUserData.freeShotCount;

  const playModeChange = async () => {
    if (user === undefined) return;
    // FSG-8906: a plain autoplay on/off toggle must NOT re-assert the wager
    // amount — it sends -1 ("keep current"). Echoing user.autoPlayAmount here
    // is ambiguous to the engine: during a balance downshift that value is the
    // reduced tier, and a concrete amount on the enable path is otherwise
    // treated as a deliberate re-pin (which voids the downshift episode). Only
    // the amount picker (challengeFeeChange / saveAutoPlaySettings) sends a
    // concrete amount, so the engine can trust a concrete value = a real choice.
    await enableAutoPlay(!user.autoPlayEnabled, -1);
  };

  const enableAutoPlay = async (enabled: boolean, fee: number): Promise<void> => {
    Context.setLoadingOverlay(true);

    try {
      await ServiceClient.enableAutoPlay(enabled, fee);
      await Context.getUser();

    } catch (err: any) {
      console.log('enableAutoPlay err', err);

      Context.setCancelModalConfig({
        visible: true, title: 'Error', subtitle: err.message, rightBtnText: 'OK',
        onResume: () => Context.setCancelModalConfig({...cancelModalConfig, visible: false})
      });
    } finally {
      Context.setLoadingOverlay(false);
    }
  };

  const adjustActiveBet = async (amount: number): Promise<boolean> => {
    if (!activeBet) return false;
    Context.setLoadingOverlay(true);

    try {
      await ServiceClient.adjustBet(activeBet.betId, amount);
      Context.getUser().catch(err => console.log('getUser', err));
      return true;

    } catch (err: any) {
      console.log('adjustBet err', err);

      Context.setCancelModalConfig({
        visible: true, title: 'Error', subtitle: err.message, rightBtnText: 'OK',
        onResume: () => Context.setCancelModalConfig({...cancelModalConfig, visible: false})
      });
      return false;

    } finally {
      Context.setLoadingOverlay(false);
    }
  };

  const showFullSwingCancelError = (subtitle: string) => {
    Context.setCancelModalConfig({
      visible: true, title: 'ERROR', subtitle, rightBtnText: 'OK',
      onResume: () => Context.setCancelModalConfig(prev => ({...prev, visible: false})),
      onClose: () => Context.setCancelModalConfig(prev => ({...prev, visible: false}))
    });
  };

  const cancelFullSwingBet = async (betId: bigint): Promise<void> => {
    if (activeBet?.betId !== betId) {
      showFullSwingCancelError('Challenge could not be cancelled. Please try again.');
      return;
    }

    const currentAuthoritativeBetId = getCurrentActiveBetId();
    const requestedBetId = String(betId);
    const isStaleBayCancellation = isFullSwing && sessionBackend === 'bay-session' && Boolean(currentAuthoritativeBetId) && currentAuthoritativeBetId !== requestedBetId;
    if (isStaleBayCancellation) {
      showFullSwingCancelError('Challenge could not be cancelled. Please try again.');
      return;
    }

    // Route by the backend that owns the station, exactly as logout does. The
    // two cancels are NOT interchangeable: bay-session CancelBet flips the bet
    // to 'refunded' with no wallet call, while smg-core RefundBet runs a real
    // pre-play wallet refund. A legacy (partner-service-managed) station also
    // has no bayPlayerJwt — the fallback login clears it — so calling the bay
    // path there can only fail.
    const isBaySession = Context.sessionBackend === 'bay-session';
    const bayPlayerJwt = Context.bayPlayerJwt;
    if (isBaySession && !bayPlayerJwt) {
      showFullSwingCancelError('Session not ready — please reload the app.');
      return;
    }

    Context.setLoadingOverlay(true);

    try {
      if (isBaySession) {
        const result = await ServiceClient.cancelBetV2(betId, bayPlayerJwt);
        if (result.betId !== String(betId) || result.betState !== 'refunded') {
          throw new CancelBetMessage('Challenge cancellation could not be confirmed. Please try again.');
        }

      } else {
        await ServiceClient.refundBet(betId);
        // Legacy has no PlayerState stream to re-project the balance, so the
        // refresh the pre-SMG-3995 CancelBet did stays on this path only.
        noteCancellation(betId);
        await Context.getUser().catch(err => console.log('getUser', err));
      }

      if (isBaySession) noteCancellation(betId);

      setBetsList(prev => prev.filter(candidate => candidate.betId !== betId));
      setActiveBet(prev => prev?.betId === betId ? undefined : prev);

    } catch (error: unknown) {
      console.log('cancelBet err', error);
      showFullSwingCancelError(error instanceof CancelBetMessage ? error.message : 'Challenge could not be cancelled. Please try again.');

    } finally {
      Context.setLoadingOverlay(false);
    }
  };

  const autoPlayHandler = () => {
    setConfirmModalType('');
    // FSG-8906: disabling autoplay is a flag-only toggle, not an amount change —
    // send -1 ("keep current") rather than echoing the (possibly downshifted)
    // displayed amount, so LastBetAmount keeps the user's real preference.
    if (user) enableAutoPlay(false, -1);
  };

  const challengeFeeChange = async (fee: string) => {
    setConfirmModalType('');
    void formik.setFieldValue('contestAmount', fee);
    const amount = fee === '-2' ? -2 : +fee * 100;
    const amountChanged = activeBet ? Number(activeBet.amount) !== amount : false;

    if (activeBet?.betState === 'accepted' && amountChanged) {
      const adjusted = await adjustActiveBet(amount);
      if (!adjusted) return;
    }

    await enableAutoPlay(true, amount);
  };

  const changeGameType = () => {
    setCurrBetType('paytable');
  };

  const checkUserAccess = () => {
    const accessBlock = getUserAccessBlock(user, isFullSwing);

    if (accessBlock === 'terms') {
      Context.setCancelModalConfig({
        visible: true, title: 'Notice', titleColor: MainStyle.c_yellow, leftBtnText: 'OK',
        subtitle: 'Please accept Terms & Conditions and complete Know Your Customer to create your account and play.',
        onExit: () => {
          Context.setCancelModalConfig({...cancelModalConfig, visible: false});
          navigation.reset({index: 0, routes: [{name: 'Register', params: {step: 2}}]});
        }
      });
      return false;

    } else if (accessBlock === 'kyc-duplicate') {
      Context.setCancelModalConfig({
        visible: true, title: 'ERROR', leftBtnText: 'OK',
        subtitle: 'Your KYC details appear to be identical to an existing registration record in our database. We have sent a message to the email originally provided with your KYC credentials suggesting to reset the PIN if you forgot it. If this does not help, You can request Support  to assist with your registration.',
        onExit: () => {
          Context.logout().then(() => {
            setTimeout(() => {
              if (Platform.OS === 'web') window.location.reload();
            }, 150);
            Context.setCancelModalConfig({...cancelModalConfig, visible: false});
            navigation.reset({index: 0, routes: [{name: StartingPage}]});
          });
        }
      });
      return false;

    } else if (accessBlock === 'kyc-mismatch') {
      Context.setCancelModalConfig({
        visible: true, title: 'ERROR', leftBtnText: 'Cancel', rightBtnText: 'Contact Support',
        subtitle: `Your Know Your Customer information and registration information do not match. Your account with ${BrandName} has been locked. Please contact support to resolve this issue.`,
        onResume: () => {
          if (Platform.OS === 'web') {
            window.open('https://smgames.atlassian.net/servicedesk/customer/portal/3', '_self');
          }
        },
        onClose: () => {
          Context.setCancelModalConfig({...cancelModalConfig, visible: false});
          navigation.navigate({name: 'account', params: {step: 0}});
        }
      });
      return false;

    } else if (accessBlock === 'photo') {
      navigation.reset({index: 0, routes: [{name: 'Register', params: {step: 3}}]});
      return false;

    } else if (accessBlock === 'under-18') {
      if (isFullSwing) {
        setShowAgeRestrictionModal(true);
      } else {
        Context.setCancelModalConfig({
          visible: true, title: 'Warning', titleColor: MainStyle.c_yellow, leftBtnText: 'OK',
          subtitle: 'You have successfully completed registration. However, you must be 18 or older to play.',
          onExit: () => {
            Context.logout().then(() => {
              setTimeout(() => {
                if (Platform.OS === 'web') window.location.reload();
              }, 150);
              Context.setCancelModalConfig({...cancelModalConfig, visible: false});
              navigation.reset({index: 0, routes: [{name: StartingPage}]});
            });
          }
        });
      }
      return false;

    } else if (accessBlock === 'self-limitation' && user) {
      Context.setCancelModalConfig({
        visible: true, title: 'Notice', titleColor: MainStyle.c_yellow, leftBtnText: 'OK',
        subtitle: `You will not be able to play or deposit money while the Self-Limitation is in effect. End of Self-Limitation on ${moment.unix(Number(user.selfLimitationExpiration)).format('ll')}`,
        onExit: () => {
          Context.setCancelModalConfig({...cancelModalConfig, visible: false});
          navigation.navigate({name: 'account', params: {step: 1}});
        }
      });
      return false;
    }

    // if (isFullSwing && !user?.photoAccepted && !activeBet) {
    //   setOpenKYCModal(true);
    // }

    return true;
  };

  const clearOpenBetRetry = () => {
    clearTimeout(openBetRetryTimeoutRef.current);
    // SMG-4201: the pause is a promise that only the timer resolves. With the timer gone the search would wait for ever,
    // its `finally` would never run and fullSwingOpenBetInFlightRef would stay true: the search could never start again.
    // Letting the pause end makes the search see the new generation (below) and stop.
    openBetRetryResolveRef.current?.();
    openBetRetryResolveRef.current = undefined;
    // SMG-4387: cancelling the pending TIMER is not enough. The retry chain is
    // an async recursion — a hop already past its timer, or one awaiting the
    // getBets promise, resumes after this call and would go on to fetch and
    // write state. Bumping the generation fences those continuations: each hop
    // re-checks it and bails. Every cleanup path and every new refresh already
    // funnels through here, including unmount, so an unmounted BetSlip can no
    // longer keep polling for open bets.
    openBetFetchGenerationRef.current += 1;
  };

  const applyVisibleBets = (res: Bet[]) => {
    const betTypes = getBetTypesFromBets(res);

    if (betTypes.length && (!currBetType || !betTypes.includes(currBetType))) setCurrBetType(betTypes[0]);

    setBetTypes(betTypes);
    setBetsList(res);

    setTimeout(() => {
      Context.setLoadingOverlay(false);
    }, 150);
  };

  const getVisibleBets = (state: 'open' | 'accepted') => {
    countLookAfterCancel(state);

    return ServiceClient.getBets(state).then(filterVisibleBets);
  };

  const handleBetsFound = (res: Bet[]) => {
    noBetsAvailableRef.current = false;
    setNoBetsAvailable(false);
    applyVisibleBets(res);

    if (extendedUserData.hasActiveTournament && !user?.autoPlayEnabled && Date.now() > autoPlayModalCooldownRef.current) {
      setContinueAutoPlay(true);
    }
  };

  const clearActiveBetState = () => {
    noBetsAvailableRef.current = true;
    setNoBetsAvailable(true);
    setBetTypes([]);
    setBetsList([]);
    setActiveBet(undefined);
    Context.setLoadingOverlay(false);
  };

  const handleGetBetsError = (err: any) => {
    console.log('getBets err', err);
    const params: any = route.params;
    Context.setLoadingOverlay(false);

    let conf: CancelModalProps = {
      visible: true, title: 'ERROR', leftBtnText: 'OK', subtitle: err.message,
      onExit: () => {
        Context.setCancelModalConfig({...cancelModalConfig, visible: false});
        navigation.navigate({name: 'account', params: {step: 1}});
      }
    };

    if (err.code === 20020054) {
      conf.leftBtnText = 'Locations';
      conf.titleColor = MainStyle.c_lightblue;
      conf.rightBtnText = 'OK';
      conf.rightBtnBackgroundColor = colors.gray;

      conf.onResume = () => {
        Context.setCancelModalConfig({...cancelModalConfig, visible: false});
        navigation.navigate({name: 'account', params: {step: 0}});
      };

      conf.onExit = () => {
        const url = `${Landing}/locations/`;
        if (Platform.OS === 'web') window.open(url, '_self');
        else Linking.openURL(url).then();
      };

      if (params && params.fromRegistrationPage) {
        conf.title = 'Congrats! You are all set.';
        conf.subtitle = `Go to your nearest ${BrandName} venue to play.`;

      } else {
        conf.title = 'Offsite Mode';
        conf.subtitle = 'While away from SMG locations, you can see your profile and account information.';
        conf.secondary = 'To enter challenges, go to your nearest location.';
      }
    }

    Context.setCancelModalConfig(conf);
  };

  const {
    refreshAcceptedBet, recordCancellation: recordFullSwingCancellation, getCurrentActiveBetId, getLiveActiveBetId,
    isCancelled: isFullSwingBetCancelled, getLastCancellation, invalidateRefreshes: invalidateFullSwingRefreshes
  } = useFullSwingAcceptedBetRefresh({
    backend: sessionBackend, activeBetId: bayActiveBetId, activeBetAmountMinor: bayActiveBetAmountMinor, activeBet,
    fetchAcceptedBets: () => getVisibleBets('accepted'), onBetsFound: handleBetsFound, onEmpty: clearActiveBetState,
    onError: handleGetBetsError
  });

  // SMG-4201: the cancel was confirmed. From now on the screen counts what the stream and the server tell it, so that a
  // long wait can be explained: did the stream clear the cancelled id, and how often did the screen look for a bet.
  const noteCancellation = (betId: bigint) => {
    recordFullSwingCancellation(betId);
    afterCancelRef.current = {
      at: Date.now(), streamIdAtAck: getCurrentActiveBetId() !== undefined, streamChanges: 0, openLooks: 0, acceptedLooks: 0
    };
  };

  const countLookAfterCancel = (state: 'open' | 'accepted') => {
    const afterCancel = afterCancelRef.current;
    if (!afterCancel) return;

    if (state === 'open') afterCancel.openLooks += 1;
    else afterCancel.acceptedLooks += 1;
  };

  // A change of the stream id or amount after the cancel, and the moment the id first went empty.
  useEffect(() => {
    const afterCancel = afterCancelRef.current;
    if (!afterCancel) return;

    afterCancel.streamChanges += 1;
    if (!bayActiveBetId && afterCancel.streamIdClearedAfterSec === undefined) {
      afterCancel.streamIdClearedAfterSec = Math.round((Date.now() - afterCancel.at) / 1000);
    }
  }, [bayActiveBetId, bayActiveBetAmountMinor]);

  const fetchOpenBetWithSoftRetry = async (retriesLeft: number): Promise<void> => {
    // SMG-4387: generation fence — see clearOpenBetRetry. Captured once for the
    // whole chain so any hop that resumes after a cancel drops out instead of
    // writing state for a screen that has moved on (or unmounted).
    const generation = openBetFetchGenerationRef.current;

    try {
      countLookAfterCancel('open');
      const raw = await ServiceClient.getBets('open');
      const visible = filterVisibleBets(raw);
      openBetLookRef.current = {rawCount: raw.length, visibleCount: visible.length};
      // SMG-4201: a bet this screen has cancelled must not come back as the "next" one if it is read as open.
      const res = visible.filter(bet => !isFullSwingBetCancelled(bet.betId));
      if (generation !== openBetFetchGenerationRef.current) return;

      if (res.length) {
        handleBetsFound(res);
        return;
      }

      // SMG-4201: another path ("Check again") may have put a bet on screen while this request was out. An empty answer
      // says nothing about that bet and must not wipe it.
      if (isFullSwing && betAppliedRef.current) return;

      openBetSearchResultRef.current = 'empty';
      clearActiveBetState();
      if (retriesLeft > 0) {
        await new Promise<void>((resolve) => {
          openBetRetryResolveRef.current = resolve;
          openBetRetryTimeoutRef.current = setTimeout(() => {
            openBetRetryResolveRef.current = undefined;
            resolve();
          }, NON_FULL_SWING_OPEN_RETRY_DELAY_MS);
        });
        if (generation !== openBetFetchGenerationRef.current) return;
        await fetchOpenBetWithSoftRetry(retriesLeft - 1);
      }
    } catch (err) {
      if (generation !== openBetFetchGenerationRef.current) return;
      openBetSearchResultRef.current = 'error';
      handleGetBetsError(err);
    }
  };

  // Pull the legacy Bet shape (challenges + paylines) used by the
  // current render path after bay-session says a FullSwing active bet changed.
  const refreshFullSwingActiveBet = () => {
    clearOpenBetRetry();
    refreshAcceptedBet();
  };

  // SMG-4387: the FullSwing OPEN-bet fallback.
  //
  // bay-session's active-bet projection filters to ('accepted','committed')
  // (store/queries/bet_active.sql), so SessionPlayer.active_bet_id is EMPTY
  // for a player whose only bet is OPEN, and the stream effect below clears
  // the local state. Until this fallback existed the FullSwing skin then had
  // no bet object at all, and FullSwingHomePage renders "Waiting for next
  // challenge…" with a spinner whenever `bet` is undefined — no accept
  // affordance, and nothing anywhere expires an OPEN bet.
  //
  // That did not matter while the engine auto-accepted for every seated
  // player. Now that the engine declines unless the player consented, an
  // opted-out player's bet legitimately sits OPEN, and the whole risk argument
  // for the consent gate is that a stale FALSE is "recoverable in one tap".
  // This is the tap: fetch the open bet so it renders and the player can
  // accept it themselves.
  //
  // Reuses the same fetch the consumer skin already runs, keeping one
  // open-bet code path across the two skins.
  const refreshFullSwingOpenBetFallback = async () => {
    if (fullSwingOpenBetInFlightRef.current) return;

    fullSwingOpenBetInFlightRef.current = true;
    try {
      await fetchOpenBetWithSoftRetry(FULL_SWING_OPEN_RETRY_ATTEMPTS);
    } finally {
      fullSwingOpenBetInFlightRef.current = false;
    }
  };

  // SMG-4201: the stream id that keeps the open-bet search off. With AutoPlay off a bet this screen cancelled does not
  // count (see the effect of the search below); with AutoPlay on every stream id does.
  const getStreamBetIdThatKeepsSearchOff = () => user?.autoPlayEnabled ? getCurrentActiveBetId() : getLiveActiveBetId();

  // SMG-4201: the next-bet wait. While a FullSwing player is active and has no bet, the screen shows a spinner. If it goes
  // on, tell the player, report it, and let the player ask again.
  betAppliedRef.current = Boolean(activeBet) || betsList.length > 0;
  const serverSaysInsufficientFunds = Boolean(insufficientFundsNoFallback) || bayAutoPlayBalanceStatus === AutoPlayBalanceStatus.AUTO_PLAY_BALANCE_INSUFFICIENT;
  const waitingForNextBet = isFullSwing && sessionBackend === 'bay-session' && !!user && isFocused && userSessionData?.userSessionState === UserSessionState.ACTIVE && !serverSaysInsufficientFunds && !getUserAccessBlock(user, isFullSwing) && !betAppliedRef.current
    // bay-session makes no bet for a player without a wager mode: there is nothing to wait for.
    && activeWagerMode !== WagerMode.NONE;
  waitingForNextBetRef.current = waitingForNextBet;

  const {stuck: nextBetWaitStuck} = useFullSwingNextBetEpisode({
    active: waitingForNextBet, hasBet: betAppliedRef.current, autoPlayEnabled: !!user?.autoPlayEnabled,
    getContext: () => {
      const streamBetId = getCurrentActiveBetId();
      const lastCancellation = getLastCancellation();
      const cancelledRecently = lastCancellation !== undefined && Date.now() - lastCancellation.at < 600_000;

      return {
        autoPlayEnabled: !!user?.autoPlayEnabled, streamBetIdPresent: streamBetId !== undefined,
        streamBetIdCancelled: streamBetId !== undefined && isFullSwingBetCancelled(streamBetId), sessionBackend,
        userSessionState: userSessionData?.userSessionState, wagerMode: String(activeWagerMode),
        cancelledBetId: cancelledRecently ? lastCancellation.betId : undefined,
        secSinceCancel: cancelledRecently ? Math.round((Date.now() - lastCancellation.at) / 1000) : undefined,
        streamIdAtCancelAck: cancelledRecently ? afterCancelRef.current?.streamIdAtAck : undefined,
        streamChangesSinceCancel: cancelledRecently ? afterCancelRef.current?.streamChanges : undefined,
        secToStreamIdClear: cancelledRecently ? afterCancelRef.current?.streamIdClearedAfterSec : undefined,
        openLooksSinceCancel: cancelledRecently ? afterCancelRef.current?.openLooks : undefined,
        acceptedLooksSinceCancel: cancelledRecently ? afterCancelRef.current?.acceptedLooks : undefined,
        oneShotResult: openBetSearchResultRef.current,
        lastOpenRawCount: openBetLookRef.current?.rawCount, lastOpenVisibleCount: openBetLookRef.current?.visibleCount
      };
    }
  });

  // SMG-4201: "Check again". The player asks once more, right now. First the accepted bet (the one the stream announced,
  // or any accepted bet if it announced none). Then, only for a player with AutoPlay off, the open bet: with AutoPlay on an
  // empty answer would make the server create a bet that is accepted and charged at once, outside the rules of bay-session.
  // Everything is quiet: the notice stays on the screen and the player may ask again.
  const [checkingNextBet, setCheckingNextBet] = useState(false);
  const checkingNextBetRef = useRef(false);
  const checkNextBetAgain = async () => {
    if (checkingNextBetRef.current) return;

    checkingNextBetRef.current = true;
    setCheckingNextBet(true);

    // An answer is used only if the player is still waiting, no other path has put a bet on the screen meanwhile, and the
    // stream has not announced another bet while the request was out.
    // A bet this screen cancelled is not a stream bet here, and is never shown again.
    const streamBetIdAtStart = getLiveActiveBetId();
    const applyIfStillWaiting = (found: Bet[]) => {
      const bets = found.filter(bet => !isFullSwingBetCancelled(bet.betId));
      if (!bets.length || unmountedRef.current || !waitingForNextBetRef.current) return false;
      if (getLiveActiveBetId() !== streamBetIdAtStart) return false;

      handleBetsFound(bets);
      return true;
    };

    try {
      reportNextBetEvent('Next Bet Check Again Tapped', {
        autoPlayEnabled: !!user?.autoPlayEnabled, streamBetIdPresent: streamBetIdAtStart !== undefined
      });

      const accepted = await withTimeout(getVisibleBets('accepted'), NEXT_BET_REQUEST_TIMEOUT_MS);
      if (applyIfStillWaiting(streamBetIdAtStart === undefined ? accepted : accepted.filter(bet => String(bet.betId) === streamBetIdAtStart))) return;

      // Read now, not from this render: the player may have switched AutoPlay on while the request was out.
      if (autoPlayEnabledRef.current) return;

      countLookAfterCancel('open');
      const raw = await withTimeout(ServiceClient.getBets('open'), NEXT_BET_REQUEST_TIMEOUT_MS);
      const visible = filterVisibleBets(raw);
      openBetLookRef.current = {rawCount: raw.length, visibleCount: visible.length};
      applyIfStillWaiting(visible);

    } catch (err) {
      console.log('check next bet err', err);

    } finally {
      checkingNextBetRef.current = false;
      setCheckingNextBet(false);
    }
  };

  // non-FullSwing has no bay-session stream. First check the accepted bet,
  // then briefly look for a new open bet because smg-core may expose it a
  // moment after the settled bet disappears.
  const refreshNonFullSwingActiveBet = async () => {
    if (nonFullSwingBetRefreshInFlightRef.current) return;

    nonFullSwingBetRefreshInFlightRef.current = true;
    clearOpenBetRetry();
    try {
      const acceptedBets = await getVisibleBets('accepted');

      if (acceptedBets.length) {
        handleBetsFound(acceptedBets);
        return;
      }

      clearActiveBetState();
      await fetchOpenBetWithSoftRetry(NON_FULL_SWING_OPEN_RETRY_ATTEMPTS);
    } catch (err) {
      handleGetBetsError(err);
    } finally {
      nonFullSwingBetRefreshInFlightRef.current = false;
    }
  };

  const scheduleNonFullSwingUserRefresh = () => {
    clearTimeout(nonFullSwingRefreshTimeoutRef.current);
    nonFullSwingRefreshTimeoutRef.current = setTimeout(() => {
      Context.getUser().catch(err => {
        console.log('getUser err', err);
        scheduleNonFullSwingUserRefresh();
      });
    }, NON_FULL_SWING_REFRESH_INTERVAL_MS);
  };

  const getUser = () => {
    Context.getUser().catch(err => {
      console.log('getUser err', err);
      setTimeout(() => {
        getUser();
      }, 5000);
    });
  };

  useEffect(() => {
    if (!activeBet) return;

    // autoPlayAmount is a preference, not always money: -1 and 0 mean
    // "no pinned amount", while -2 means "use max".
    const autoPlayAmount = user?.autoPlayEnabled ? user.autoPlayAmount : undefined;
    const currentAmount = autoPlayAmount === -2 || (autoPlayAmount !== undefined && autoPlayAmount > 0) ? autoPlayAmount : Number(activeBet.amount);
    const amount = isFreePlay ? 3 : currentAmount === -2 ? -2 : currentAmount / 100;

    formik.setFieldValue('contestAmount', `${amount}`);
  }, [activeBet?.betId, activeBet?.amount, isFreePlay, user?.autoPlayAmount, user?.autoPlayEnabled]);

  useEffect(() => {
    if (activeBet) {
      if (extendedUserData.isNewPlayer) {
        (async () => {
          let newPlayers = await AsyncStorage.getItem('newPlayers');

          if (!newPlayers || !newPlayers?.includes(`${user?.userId}`)) {
            setAlertModalConfig({
              isOpen: true, title: 'Build Your Bank!', message: 'You have 10 Free Shots to win as much as possible.',
              firstButtonText: 'Start Now', hideSecondButton: true
            });

            await AsyncStorage.setItem('newPlayers', newPlayers ? newPlayers + `${user?.userId}, ` : `${user?.userId}, `);
          }
        })();

      } else if ((extendedUserData.freeShotNumber === extendedUserData.freeShotCount) && !user?.autoPlayEnabled && !init.current) {
        setConfirmModalType('StartAutoPlay');
        init.current = true;
      }
    }
  }, [activeBet?.betId]);

  useEffect(() => {
    const prev = lastAcceptedBetRef.current;
    if (prev && prev.betState === 'accepted') {
      if (!activeBet || activeBet.betId !== prev.betId) {
        const outcome = prev.outcome;
        const multiplier = prev.multiplier;
        const properties: Record<string, unknown> = {};
        if (outcome === 'win' || outcome === 'loss' || outcome === 'partial_win') {
          properties.outcome = outcome as 'win' | 'loss' | 'partial_win';
        }
        if (multiplier) {
          properties.multiplier = multiplier;
          if (prev.amount) properties.payout = Number(prev.amount) * multiplier;
        }
        track('Bet Result Displayed', properties);
      }
    }
    lastAcceptedBetRef.current = activeBet;
  }, [activeBet]);

  useEffect(() => {
    if (betsList.length && currBetType) {
      const val = betsList.find(el => el.betType === currBetType);
      setActiveBet(val);
    }
  }, [betsList, currBetType]);

  // SMG-3006: FullSwing gets live active-bet updates from bayActiveBetId.
  // Keep this path scoped to the logged-in user so bay-session stream-driven
  // Context.user updates do not re-fetch the legacy Bet shape every frame.
  //
  // SMG-3116: dropped the 10s `getUser` poll that used to re-issue
  // smg-core's GetUser RPC every 10s purely to refresh
  // autoPlay* fields. Main.tsx's PlayerStateUpdate dispatcher now
  // bridges SessionPlayer's autoplay / free-play / skill-index
  // projections directly into Context.user + Context.extendedUserData
  // on every stream frame (sub-second latency), so the poll was
  // wasteful — same data, slower, more wire traffic. Initial fetch
  // still runs via Context.getUser on login / post-registration to
  // seed identity fields (firstname, email, tncAccepted) the stream
  // doesn't carry.
  useEffect(() => {
    if (!isFullSwing || !user || !isFocused) return;

    if (checkUserAccess()) {
      if (sessionBackend === 'bay-session' && !getCurrentActiveBetId()) return;
      refreshFullSwingActiveBet();
    } else {
      Context.setLoadingOverlay(false);
    }

    return () => {
      clearOpenBetRetry();
    };
  }, [user?.userId, sessionBackend, isFocused]);

  // non-FullSwing has no bay-session player-state stream. While focused,
  // refresh Context.user; the refreshed user object re-runs this effect,
  // so access checks and active-bet refresh stay ordered.
  useEffect(() => {
    if (isFullSwing || !user || !isFocused) return;

    if (checkUserAccess()) {
      refreshNonFullSwingActiveBet();
      scheduleNonFullSwingUserRefresh();
    } else {
      Context.setLoadingOverlay(false);
    }
  }, [user, isFocused]);

  useEffect(() => {
    if (!isFullSwing && isFocused) return;

    clearOpenBetRetry();
    clearTimeout(nonFullSwingRefreshTimeoutRef.current);
    nonFullSwingBetRefreshInFlightRef.current = false;
    fullSwingOpenBetInFlightRef.current = false;
  }, [isFocused]);

  useEffect(() => () => {
    clearOpenBetRetry();
    clearTimeout(nonFullSwingRefreshTimeoutRef.current);
    nonFullSwingBetRefreshInFlightRef.current = false;
    fullSwingOpenBetInFlightRef.current = false;
  }, []);

  // SMG-3006: stream-driven re-fetch. When bay-session publishes a
  // PLAYER_BET_ACCEPTED (or any other event that mutates the
  // SessionPlayer.active_bet_id field — e.g. ProcessShot post-settle
  // auto-accepts the next bet), Context.bayActiveBetId changes and
  // BetSlip re-fetches the legacy Bet shape from smg-core. One RPC
  // per bet, no polling. Empty string means "no active bet" — clear
  // the local state without fetching.
  //
  // bayActiveBetAmountMinor is a dep too: a wager RESIZE re-amounts the
  // bet IN PLACE (same bet_id), so the id alone never signals it and
  // activeBet.amount went stale until the next bet was created. The
  // bridged per-frame amount is the stream signal that the bet object
  // needs re-fetching; everything rendered from activeBet then converges
  // to the server truth.
  useEffect(() => {
    if (!isFullSwing || sessionBackend !== 'bay-session') return;
    if (!user || !isFocused) return;
    const currentAuthoritativeBetId = getCurrentActiveBetId();
    if (!currentAuthoritativeBetId) {
      invalidateFullSwingRefreshes();
      // Stream says no ACCEPTED/COMMITTED bet → clear the local BetSlip state.
      // SMG-4387: that is NOT the same as "no bet" — see the open-bet fallback
      // effect below, which owns looking for one.
      setBetsList([]);
      setActiveBet(undefined);
      return;
    }
    refreshFullSwingActiveBet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bayActiveBetId, bayActiveBetAmountMinor, sessionBackend, user?.userId, isFocused]);

  // SMG-4387: the FullSwing OPEN-bet fallback.
  //
  // bay-session's active-bet projection filters to ('accepted','committed')
  // (store/queries/bet_active.sql), so SessionPlayer.active_bet_id is EMPTY for
  // a player whose only bet is OPEN, and the effect above clears local state.
  // The FullSwing skin then has no bet object at all, and FullSwingHomePage
  // renders "Waiting for next challenge…" with a spinner whenever `bet` is
  // undefined — no accept affordance — while nothing anywhere expires an OPEN
  // bet. The player waits forever.
  //
  // That never surfaced while the engine auto-accepted for every seated player.
  // Now that it declines unless the player consented, an opted-out player's bet
  // legitimately sits OPEN, and the entire risk argument for the consent gate is
  // that a stale FALSE is "recoverable in one tap". This is the tap: fetch the
  // open bet so it renders and the player can accept it themselves.
  //
  // Fetching it is only HALF the tap, and review round 7 caught the other half
  // missing: FullSwingHomePage gated its whole bet-interaction slot on
  // isSingleShot (= !confirmationBypassEnabled), so for a confirm-bypass player
  // this fetch made the bet defined and the screen then rendered nothing for
  // it — no accept button, no Cancel, worse than the spinner it replaced. That
  // gate now also admits `betState === 'open'`, and
  // FullSwingHomePage.smg4387-open-bet-accept.test.tsx seals both cohorts
  // against the REAL screen. Making an object visible to a screen is not the
  // same as the screen offering an action on it.
  //
  // Deliberately its OWN effect rather than a branch of the stream effect: the
  // trigger is "there is no bet to show", which is a function of the local
  // activeBet, not of a particular stream frame. Keying it on activeBet also
  // means a fetch that arrives late and populates the bet stops the retries by
  // itself, and the accepted-refresh staleness fences above stay untouched.
  useEffect(() => {
    if (!isFullSwing || sessionBackend !== 'bay-session') return;
    if (!user || !isFocused) return;
    // Something is already on screen, or the stream has an accepted bet
    // inbound — either way there is nothing to recover from.
    //
    // SMG-4201: with AutoPlay off, the id of a bet this screen has just cancelled is NOT a bet inbound: the frame that
    // clears it is best effort, and while the stale id sat in the stream the fallback stayed off and the player waited on
    // the spinner. With AutoPlay on any stream id keeps the fallback off, as before: there the server makes the next bet
    // itself, and a look for an open bet right after a cancel would make the server create one that is accepted and
    // charged at once, outside the rules of bay-session.
    if (activeBet || getStreamBetIdThatKeepsSearchOff()) return;

    void refreshFullSwingOpenBetFallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bayActiveBetId, activeBet, sessionBackend, user?.userId, isFocused]);

  useIdleLogout({enabled: isFocused, onLogout: () => Context.setLoadingOverlay(false)});

  useLayoutEffect(() => {
    if (activeBet) Context.setLoadingOverlay(true);

    (async () => {
      const time = await AsyncStorage.getItem('lastActiveTime');

      if (time && moment().diff(moment(time), 'minutes') > SESSION_EXPIRE_TIME) {
        Context.logout().then(() => {
          if (Platform.OS === 'web') window.open(LANDING_PAGE_URL, '_self');
          Context.setLoadingOverlay(false);
        });

      } else {
        const val = await AsyncStorage.getItem('session');
        let curUser: User | undefined;

        if (val) {
          const Session = JSON.parse(val);
          Context.setSession(Session);

          try {
            curUser = await Context.getUser(Session);

          } catch (e: any) {
            console.log('get curUser err', e);
            Context.setCancelModalConfig({
              visible: true, title: 'Error', subtitle: e?.message || 'Error', rightBtnText: 'OK',
              onResume: () => {
                Context.setCancelModalConfig({...cancelModalConfig, visible: false});
                Context.logout().then(() => {
                  if (Platform.OS === 'web') window.open(LANDING_PAGE_URL, '_self');
                  Context.setLoadingOverlay(false);
                });
              }
            });
          }
        }

        if (!val || !curUser || !curUser.userId) {
          Context.logout().then(() => {
            if (Platform.OS === 'web') window.open(LANDING_PAGE_URL, '_self');
            Context.setLoadingOverlay(false);
          });

        } else console.log('user', curUser);
      }
    })();
  }, []);

  return (
    <>
      {isFullSwing ? (
        <FullSwingHomePage
          bet={activeBet} formik={formik} playModeChange={playModeChange} saveAutoPlaySettings={enableAutoPlay}
          onCancelBet={cancelFullSwingBet} showStuckNotice={nextBetWaitStuck} onCheckAgain={checkNextBetAgain}
          checkingNextBet={checkingNextBet}
        />
      ) : (
        <>
          <Div flex={1} pt={insets.top}>
            <Div flex={1}>
              {(!!activeBet && activeBet.betState === 'open') ? (
                <OpenBets
                  bet={activeBet} formik={formik} title="New Challenge" type="open" changeGameType={changeGameType}
                  moreGames={BetTypes.length > 1}
                  showAutoPlayModal={val => setConfirmModalType(val ? val : user?.autoPlayEnabled ? 'StopAutoPlay' : 'StartAutoPlay')}
                />
              ) : (!!activeBet && activeBet.betState === 'accepted') ? (
                <OpenBets
                  bet={activeBet} title="Live Challenge" type="accepted"
                  showAutoPlayModal={val => setConfirmModalType(val ? val : user?.autoPlayEnabled ? 'StopAutoPlay' : 'StartAutoPlay')}
                  formik={formik}
                />
              ) : noBetsAvailable ? (
                <>
                  <NewHeader/>
                  <ProtectionAndDisclaimer isLg={isMd} text="Waiting for challenge..."/>
                </>
              ) : (
                <>
                  <NewHeader/>
                  <ProtectionAndDisclaimer isLg={isMd} text="Loading"/>
                </>
              )}
            </Div>

            {/*<Button*/}
            {/*  text="MORE GAMES" style={[styles.more_btn_m, styles.stickyBtn_md]} bg={colors.unselect}*/}
            {/*  textStyle={[MainStyle.t_a_c, MainStyle.f_s_18]} onPress={changeGameType} w={150}*/}
            {/*/>*/}

            <BottomNavigation page="challenges"/>
          </Div>

          {!!formik.values.contestAmount && (
            <TournamentRegistrationConfirmationModal
              isOpen={!!confirmModalType} onClose={() => setConfirmModalType('')} joinTournament={challengeFeeChange}
              type={confirmModalType} entryFee={0} entryChallengeFee={`${+formik.values.contestAmount * 100}`}
              autoPlayHandler={autoPlayHandler} min_h={confirmModalType === 'StopAutoPlay' ? 372 : undefined}
            />
          )}

          <ContinueAutoPlayModal
            isOpen={continueAutoPlay} onReload={getUser}
            onClose={() => {
              setContinueAutoPlay(false);
              autoPlayModalCooldownRef.current = Date.now() + AUTO_PLAY_MODAL_COOLDOWN_MS;
            }}
          />

          <AlertModal
            isOpen={alertModalConfig.isOpen}
            onClose={() => setAlertModalConfig(prevState => ({...prevState, isOpen: false}))}
            message={alertModalConfig.message} title={alertModalConfig.title}
            onContinue={() => setAlertModalConfig(prevState => ({...prevState, isOpen: false}))}
            firstButtonText={alertModalConfig.firstButtonText} hideSecondButton={alertModalConfig.hideSecondButton}
          />
        </>
      )}

      <VerifyIdentityModal
        visible={openKYCModal} onRequestClose={() => setOpenKYCModal(false)}
        onConfirm={() => {
          setOpenKYCModal(false);
          navigation.navigate('Register', {step: 3});
        }}
      />

      <ShortTimerModal
        visible={openShortTimerModal}
        onRequestClose={keepPlaying => {
          if (keepPlaying) AsyncStorage.setItem('lastActiveTime', moment().format()).then();
          setOpenShortTimerModal(false);
        }}
      />

      <FullSwingInfoModal
        info={{
          visible: showAgeRestrictionModal, title: "Age Restriction", warnIcon: true, showCloseIcon: true,
          subtitle: "You must be 18 or older to play Skill Strike.\nIf you feel this is an error, please update your date of birth in your profile.",
        }}
        onRequestClose={() => setShowAgeRestrictionModal(false)}
      />
    </>
  );
};

export default BetSlip;
